import React from 'react';
import { Box, Stack, IconButton, Typography, Button, TextField, alpha, useTheme, Grid } from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, CalendarMonth as CalendarMonthIcon } from '@mui/icons-material';
import ExpectedProfitLabel from './helpers/ExpectedProfitLabel';
import FormInput from '../../inputs/FormInput';

const RoomAssignmentCard = ({
    assignment,
    hotelId,
    extraOptions,
    extraOptionsForSelect,
    roomCategoriesOptions,
    creatingCategory,
    isArabic,
    onUpdateRoomStay,
    onUpdateRoomStayExtra,
    onRemoveRoomStayExtra,
    onRemoveRoomAssignment,
    onUpdateAssignmentDates,
    onAddExtraToAllStays,
    onCreateCategory,
    errors,
    hotel,
    isLastAssignment = false,
}) => {
    const theme = useTheme();

    // حساب إجمالي الكوست والسيل والأرباح
    const costPerNight = parseFloat(assignment.custom_cost_price) || 0;
    const sellPerNight = parseFloat(assignment.custom_sell_price) || 0;
    const nights = assignment.nights || 0;

    const totalCost = costPerNight * nights;
    const totalSell = sellPerNight * nights;
    const profit = totalSell - totalCost;

    const getAssignmentError = (fieldSuffix) => {
        if (!errors || !hotel?.room_stays) return '';
        for (const rs of assignment.stays) {
            const rsIndex = hotel.room_stays.findIndex(r => r.id === rs.id);
            if (rsIndex !== -1) {
                const key = `hotel_${hotelId}_rs_${rsIndex}_${fieldSuffix}`;
                if (errors[key]) return errors[key];
            }
        }
        return '';
    };

    return (
        <Box
            sx={{
                mb: 1.5, p: 1.5, borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                bgcolor: alpha(theme.palette.background.paper, 0.4),
            }}
        >
            {/* Assignment header: date range + delete */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <CalendarMonthIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormInput
                        type="date"
                        name={`ass_from_${hotelId}_${assignment.id}`}
                        value={assignment.from_date}
                        onChange={(e) => {
                            if (e.target.value) {
                                onUpdateAssignmentDates(hotelId, assignment, e.target.value, assignment.to_date);
                            }
                        }}
                        min={hotel?.check_in_date}
                        max={assignment.to_date}
                        size="small"
                        sx={{ width: 140, '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8rem' } }}
                    />
                    <Typography variant="caption" color="text.secondary">→</Typography>
                    <FormInput
                        type="date"
                        name={`ass_to_${hotelId}_${assignment.id}`}
                        value={assignment.to_date}
                        onChange={(e) => {
                            if (e.target.value) {
                                onUpdateAssignmentDates(hotelId, assignment, assignment.from_date, e.target.value);
                            }
                        }}
                        min={assignment.from_date}
                        max={hotel?.check_out_date}
                        size="small"
                        sx={{ width: 140, '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8rem' } }}
                    />
                </Box>
                <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem', ml: 1, color: 'text.secondary' }}>
                    ({assignment.nights} {isArabic ? 'ليالي' : 'nights'})
                </Typography>
                <Box sx={{ flex: 1 }} />
                <IconButton size="small" color="error" disabled={isLastAssignment} onClick={() => onRemoveRoomAssignment(hotelId, assignment)}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Stack>

            {/* Assignment fields (Restructured to 2 rows of equal sizes) */}
            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid item xs={6}>
                    <FormInput
                        label={isArabic ? 'فئة الغرفة' : 'Room Category'}
                        type="select"
                        name={`ass_cat_${hotelId}_${assignment.id}`}
                        value={assignment.room_category_id || ''}
                        onChange={(e) => {
                            const val = e.target.value;
                            assignment.stays.forEach(rs => onUpdateRoomStay(hotelId, rs.id, 'room_category_id', val));
                        }}
                        options={roomCategoriesOptions}
                        creatable
                        disabled={creatingCategory !== null}
                        loading={creatingCategory === `${hotelId}_${assignment.id}`}
                        onCreate={(inputValue) => onCreateCategory(hotelId, assignment.id, inputValue)}
                        size="small"
                        sx={{ width: '100%' }}
                        error={!!getAssignmentError('room_category')}
                        helperText={getAssignmentError('room_category')}
                    />
                </Grid>
                <Grid item xs={6}>
                    <FormInput
                        label={isArabic ? 'العدد' : 'Rooms'}
                        type="number"
                        name={`ass_count_${hotelId}_${assignment.id}`}
                        value={assignment.rooms_count}
                        onChange={(e) => {
                            const val = e.target.value === '' ? '' : e.target.value;
                            assignment.stays.forEach(rs => onUpdateRoomStay(hotelId, rs.id, 'rooms_count', val));
                        }}
                        inputProps={{ min: 1 }}
                        size="small"
                        sx={{ width: '100%' }}
                        error={!!getAssignmentError('rooms_count')}
                        helperText={getAssignmentError('rooms_count')}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }} flexWrap="wrap">
                        <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                            {isArabic ? 'التكلفة' : 'Cost Price'}
                        </Typography>
                        <Box
                            sx={{
                                px: 0.8, py: 0.25, borderRadius: '4px',
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                fontSize: '0.75rem', fontWeight: 600, color: 'info.main',
                                lineHeight: 1.4, whiteSpace: 'nowrap',
                            }}
                        >
                            {costPerNight} × {nights} {isArabic ? 'ليلة' : 'nights'}
                        </Box>
                        <Box
                            sx={{
                                px: 0.8, py: 0.25, borderRadius: '4px',
                                bgcolor: alpha(theme.palette.info.main, 0.12),
                                fontSize: '0.8rem', fontWeight: 700, color: 'info.dark',
                                lineHeight: 1.4,
                            }}
                        >
                            = {totalCost}
                        </Box>
                    </Stack>
                    <TextField
                        type="number"
                        size="small"
                        value={assignment.custom_cost_price ?? ''}
                        onChange={(e) => {
                            const val = e.target.value === '' ? '' : e.target.value;
                            assignment.stays.forEach(rs => onUpdateRoomStay(hotelId, rs.id, 'custom_cost_price', val));
                        }}
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{ width: '100%', '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' } }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }} flexWrap="wrap">
                        <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                            {isArabic ? 'سعر البيع' : 'Sell Price'}
                        </Typography>
                        <Box
                            sx={{
                                px: 0.8, py: 0.25, borderRadius: '4px',
                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                fontSize: '0.75rem', fontWeight: 600, color: 'success.main',
                                lineHeight: 1.4, whiteSpace: 'nowrap',
                            }}
                        >
                            {sellPerNight} × {nights} {isArabic ? 'ليلة' : 'nights'}
                        </Box>
                        <Box
                            sx={{
                                px: 0.8, py: 0.25, borderRadius: '4px',
                                bgcolor: alpha(theme.palette.success.main, 0.12),
                                fontSize: '0.8rem', fontWeight: 700, color: 'success.dark',
                                lineHeight: 1.4,
                            }}
                        >
                            = {totalSell}
                        </Box>
                    </Stack>
                    <TextField
                        type="number"
                        size="small"
                        value={assignment.custom_sell_price ?? ''}
                        onChange={(e) => {
                            const val = e.target.value === '' ? '' : e.target.value;
                            assignment.stays.forEach(rs => onUpdateRoomStay(hotelId, rs.id, 'custom_sell_price', val));
                        }}
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{ width: '100%', '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' } }}
                    />
                </Grid>
            </Grid>

            <ExpectedProfitLabel
                profit={profit}
                isArabic={isArabic}
            />

            {/* Assignment extras */}
            <Box sx={{ pl: 1 }}>
                {(assignment.extras || []).map((ex, exIdx) => (
                    <Box
                        key={ex.id}
                        sx={{
                            p: 1, mb: 1, borderRadius: 1,
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                            border: `1px dashed ${alpha(theme.palette.divider, 0.4)}`,
                        }}
                    >
                        <Grid container spacing={1.5} alignItems="center">
                            <Grid item xs={8}>
                                <FormInput
                                    label={isArabic ? 'الإضافة' : 'Extra'}
                                    type="select"
                                    name={`ass_ext_${hotelId}_${assignment.id}_${exIdx}`}
                                    value={ex.hotel_extra_id || ''}
                                    onChange={(e) => {
                                        const selectedId = e.target.value;
                                        const found = extraOptions?.find(o => String(o.value ?? o.id) === String(selectedId));
                                        assignment.stays.forEach(rs => {
                                            const exOnRs = (rs.extras || [])[exIdx];
                                            if (exOnRs) {
                                                onUpdateRoomStayExtra(hotelId, rs.id, exOnRs.id, 'hotel_extra_id', selectedId);
                                                onUpdateRoomStayExtra(hotelId, rs.id, exOnRs.id, 'name', found?.label || found?.name || '');
                                                onUpdateRoomStayExtra(hotelId, rs.id, exOnRs.id, 'pricing_basis', found?.default_pricing_basis || 'once');
                                                onUpdateRoomStayExtra(hotelId, rs.id, exOnRs.id, 'custom_sell_price', parseFloat(found?.default_sell_price) || 0);
                                                onUpdateRoomStayExtra(hotelId, rs.id, exOnRs.id, 'custom_cost_price', parseFloat(found?.default_cost_price) || 0);
                                            }
                                        });
                                    }}
                                    options={extraOptionsForSelect}
                                    size="small"
                                    sx={{ width: '100%' }}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <FormInput
                                    label={isArabic ? 'الكمية' : 'Qty'}
                                    type="number"
                                    name={`ass_ext_qty_${hotelId}_${assignment.id}_${exIdx}`}
                                    value={ex.quantity}
                                    onChange={(e) => {
                                        const val = e.target.value === '' ? '' : e.target.value;
                                        assignment.stays.forEach(rs => {
                                            const exOnRs = (rs.extras || [])[exIdx];
                                            if (exOnRs) onUpdateRoomStayExtra(hotelId, rs.id, exOnRs.id, 'quantity', val);
                                        });
                                    }}
                                    inputProps={{ min: 1 }}
                                    size="small"
                                    sx={{ width: '100%' }}
                                    error={!!getAssignmentError(`extra_${exIdx}_quantity`)}
                                    helperText={getAssignmentError(`extra_${exIdx}_quantity`)}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormInput
                                    label={isArabic ? 'ملاحظة (عربي)' : 'Note (Ar)'}
                                    type="text"
                                    required={false}
                                    name={`ass_ext_note_ar_${hotelId}_${assignment.id}_${exIdx}`}
                                    value={ex.note?.ar || ''}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        assignment.stays.forEach(rs => {
                                            const exOnRs = (rs.extras || [])[exIdx];
                                            if (exOnRs) {
                                                const currentNote = exOnRs.note || { ar: '', en: '' };
                                                onUpdateRoomStayExtra(hotelId, rs.id, exOnRs.id, 'note', { ...currentNote, ar: val });
                                            }
                                        });
                                    }}
                                    size="small"
                                    sx={{ width: '100%' }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormInput
                                    label={isArabic ? 'ملاحظة (إنجليزي)' : 'Note (En)'}
                                    type="text"
                                    required={false}
                                    name={`ass_ext_note_en_${hotelId}_${assignment.id}_${exIdx}`}
                                    value={ex.note?.en || ''}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        assignment.stays.forEach(rs => {
                                            const exOnRs = (rs.extras || [])[exIdx];
                                            if (exOnRs) {
                                                const currentNote = exOnRs.note || { ar: '', en: '' };
                                                onUpdateRoomStayExtra(hotelId, rs.id, exOnRs.id, 'note', { ...currentNote, en: val });
                                            }
                                        });
                                    }}
                                    size="small"
                                    sx={{ width: '100%' }}
                                />
                            </Grid>
                            <Grid item xs={5}>
                                <FormInput
                                    label={isArabic ? 'التكلفة' : 'Cost'}
                                    type="number"
                                    name={`ass_ext_cost_${hotelId}_${assignment.id}_${exIdx}`}
                                    value={ex.custom_cost_price}
                                    onChange={(e) => {
                                        const val = e.target.value === '' ? '' : e.target.value;
                                        assignment.stays.forEach(rs => {
                                            const exOnRs = (rs.extras || [])[exIdx];
                                            if (exOnRs) onUpdateRoomStayExtra(hotelId, rs.id, exOnRs.id, 'custom_cost_price', val);
                                        });
                                    }}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    size="small"
                                    sx={{ width: '100%' }}
                                />
                            </Grid>
                            <Grid item xs={5}>
                                <FormInput
                                    label={isArabic ? 'سعر البيع' : 'Sell'}
                                    type="number"
                                    name={`ass_ext_sell_${hotelId}_${assignment.id}_${exIdx}`}
                                    value={ex.custom_sell_price}
                                    onChange={(e) => {
                                        const val = e.target.value === '' ? '' : e.target.value;
                                        assignment.stays.forEach(rs => {
                                            const exOnRs = (rs.extras || [])[exIdx];
                                            if (exOnRs) onUpdateRoomStayExtra(hotelId, rs.id, exOnRs.id, 'custom_sell_price', val);
                                        });
                                    }}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    size="small"
                                    sx={{ width: '100%' }}
                                />
                            </Grid>
                            <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'center', pt: 2.5 }}>
                                <IconButton size="small" color="error" onClick={() => {
                                    assignment.stays.forEach(rs => {
                                        const exOnRs = (rs.extras || [])[exIdx];
                                        if (exOnRs) onRemoveRoomStayExtra(hotelId, rs.id, exOnRs.id);
                                    });
                                }}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Grid>
                        </Grid>
                    </Box>
                ))}
                <Button
                    size="small"
                    variant="text"
                    startIcon={<AddIcon />}
                    onClick={() => onAddExtraToAllStays(hotelId, assignment)}
                    sx={{ fontSize: '0.7rem', py: 0.25 }}
                >
                    {isArabic ? 'إضافة خدمة' : 'Add Extra'}
                </Button>
            </Box>
        </Box>
    );
};

export default RoomAssignmentCard;